/**
 * Folder Browser Component
 * Tree-view component for browsing SharePoint folders
 */

import { useState, useEffect, useCallback } from 'react';
import {
  sharePointApi,
  SharePointFolder,
  SharePointFile,
  SyncItem,
} from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface FolderBrowserProps {
  onSelectionChange?: (items: SyncItem[]) => void;
  initialPath?: string;
}

export default function FolderBrowser({
  onSelectionChange,
  initialPath = '/',
}: FolderBrowserProps) {
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [parentPath, setParentPath] = useState('/');
  const [folders, setFolders] = useState<SharePointFolder[]>([]);
  const [files, setFiles] = useState<SharePointFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Map<string, SyncItem>>(
    new Map()
  );

  const loadFolder = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await sharePointApi.getFolders(path);
      setFolders(data.folders);
      setFiles(data.files);
      setCurrentPath(data.current_path);
      setParentPath(data.parent_path);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFolder(currentPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateToFolder = (path: string) => {
    loadFolder(path);
  };

  const toggleSelection = (
    item: SharePointFolder | SharePointFile,
    type: 'folder' | 'file'
  ) => {
    const key = `${type}-${item.id}`;
    const newSelection = new Map(selectedItems);

    if (newSelection.has(key)) {
      newSelection.delete(key);
    } else {
      newSelection.set(key, {
        type,
        id: item.id,
        path: item.path,
        name: item.name,
      });
    }

    setSelectedItems(newSelection);
    onSelectionChange?.(Array.from(newSelection.values()));
  };

  const isSelected = (id: string, type: 'folder' | 'file') => {
    return selectedItems.has(`${type}-${id}`);
  };

  const selectAll = () => {
    const newSelection = new Map<string, SyncItem>();
    folders.forEach((f) =>
      newSelection.set(`folder-${f.id}`, {
        type: 'folder',
        id: f.id,
        path: f.path,
        name: f.name,
      })
    );
    files
      .filter((f) => f.name.endsWith('.xlsx'))
      .forEach((f) =>
        newSelection.set(`file-${f.id}`, {
          type: 'file',
          id: f.id,
          path: f.path,
          name: f.name,
        })
      );
    setSelectedItems(newSelection);
    onSelectionChange?.(Array.from(newSelection.values()));
  };

  const clearSelection = () => {
    setSelectedItems(new Map());
    onSelectionChange?.([]);
  };

  // Filter Excel files for display
  const excelFiles = files.filter((f) => f.name.endsWith('.xlsx'));

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => loadFolder(currentPath)}
          className="mt-2 text-sm text-red-500 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-white">
      {/* Breadcrumb / Navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigateToFolder(parentPath)}
            disabled={currentPath === '/'}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Go to parent folder"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-600 font-mono">{currentPath}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={selectAll}
            className="text-xs text-brand-600 hover:underline"
          >
            Select All
          </button>
          <button
            onClick={clearSelection}
            className="text-xs text-gray-500 hover:underline"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Folder/File List */}
      <div className="divide-y max-h-96 overflow-y-auto">
        {folders.map((folder) => (
          <div
            key={folder.id}
            className="flex items-center px-4 py-2 hover:bg-gray-50"
          >
            <input
              type="checkbox"
              checked={isSelected(folder.id, 'folder')}
              onChange={() => toggleSelection(folder, 'folder')}
              className="mr-3 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              aria-label={`Select folder ${folder.name}`}
            />
            <div
              className="flex-1 cursor-pointer"
              onClick={() => navigateToFolder(folder.path)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigateToFolder(folder.path);
                }
              }}
            >
              <div className="flex items-center gap-2">
                <FolderIcon className="w-5 h-5 text-yellow-500" />
                <span className="font-medium">{folder.name}</span>
                <span className="text-xs text-gray-400">
                  ({folder.child_count} items)
                </span>
              </div>
            </div>
          </div>
        ))}

        {excelFiles.map((file) => (
          <div
            key={file.id}
            className="flex items-center px-4 py-2 hover:bg-gray-50"
          >
            <input
              type="checkbox"
              checked={isSelected(file.id, 'file')}
              onChange={() => toggleSelection(file, 'file')}
              className="mr-3 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              aria-label={`Select file ${file.name}`}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <FileIcon className="w-5 h-5 text-green-600" />
                <span>{file.name}</span>
                <span className="text-xs text-gray-400">
                  {formatFileSize(file.size)}
                </span>
              </div>
            </div>
          </div>
        ))}

        {folders.length === 0 && excelFiles.length === 0 && (
          <div className="px-4 py-8 text-center text-gray-500">
            No folders or Excel files found in this location.
          </div>
        )}
      </div>

      {/* Selection Summary */}
      {selectedItems.size > 0 && (
        <div className="px-4 py-2 border-t bg-brand-50 text-sm text-brand-700">
          {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Helper Functions
// =============================================================================

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// =============================================================================
// Icons
// =============================================================================

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    </svg>
  );
}

function FolderIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
    </svg>
  );
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
        clipRule="evenodd"
      />
    </svg>
  );
}
